import { PropsWithChildren, ReactNode, useState } from "react";
import { Alert, AlertProps, DialogContent as MuiDialogContent, Tab, Tabs } from "@mui/material";
import Flex from "../flex";
import { BaseDiagloHeader, ExtendedBaseDialogProps } from ".";
import { getAlertDialogConfig } from "./util";

interface BasicDialogLayoutProps extends ExtendedBaseDialogProps {
  contentPadding: string;
  headerPadding: string;
  dialogAlert?: { type: AlertProps["severity"]; message: string };
  title?: string;
  description?: string;
  titleImage?: ReactNode;
  productPreviews?: ReactNode;
}

export const BasicDialogLayout = ({
  contentPadding,
  headerPadding,
  dialogAlert,
  title,
  description,
  titleImage,
  productPreviews,
  children
}: PropsWithChildren<BasicDialogLayoutProps>): React.JSX.Element => {
  return (
    <>
      {dialogAlert && (
        <Alert {...getAlertDialogConfig(dialogAlert.type)} sx={{ padding: "14px 32px", margin: 0 }}>
          {dialogAlert.message}
        </Alert>
      )}
      {(title || description) && (
        <BaseDiagloHeader
          title={title}
          titleImage={titleImage}
          description={description}
          productPreviews={productPreviews}
          contentPadding={contentPadding}
          headerPadding={headerPadding}
        />
      )}
      <MuiDialogContent sx={{ px: contentPadding }}>{children}</MuiDialogContent>
    </>
  );
};

interface TabDialogProps extends ExtendedBaseDialogProps {
  contentPadding: string;
  tabs: Array<{ label: string | ReactNode; content: ReactNode; title?: string; description?: string }>;
}

export const TabDialogLayout = ({
  contentPadding,
  tabs,
  dialogAlert
}: PropsWithChildren<TabDialogProps>): React.JSX.Element => {
  const [activeTab, setActiveTab] = useState<number>(0);

  return (
    <Flex flexDirection="row" flex={1} overflow="hidden">
      <Tabs
        orientation="vertical"
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        sx={{
          maxWidth: "248px",
          padding: "40px 0px",
          borderRight: "1px solid",
          borderColor: "divider",
          "& .MuiTabs-indicator": {
            left: 0,
            right: "auto",
            width: "3px"
          }
        }}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={index}
            label={tab.label}
            sx={{
              padding: "8px 16px",
              "&.Mui-selected": {
                backgroundColor: "action.selected"
              }
            }}
          />
        ))}
      </Tabs>
      <Flex flexDirection="column" flex={1} overflow="hidden">
        {dialogAlert && (
          <Alert {...getAlertDialogConfig(dialogAlert.type)} sx={{ padding: "14px 32px", margin: 0 }}>
            {dialogAlert.message}
          </Alert>
        )}
        <BaseDiagloHeader
          title={tabs[activeTab]?.title}
          description={tabs[activeTab]?.description}
          contentPadding={contentPadding}
          headerPadding={"16px"}
        />
        <MuiDialogContent
          sx={{
            px: contentPadding,
            py: "16px",
            flex: 1,
            overflow: "auto",
            minHeight: 0
          }}
        >
          {tabs[activeTab]?.content}
        </MuiDialogContent>
      </Flex>
    </Flex>
  );
};
